import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Set up axios defaults
  axios.defaults.baseURL = process.env.REACT_APP_API_URL;
  console.log(axios.defaults.baseURL);
  // Add token to requests if it exists
  axios.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Handle token expiration
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        const wasRemembered = localStorage.getItem('rememberMe') === 'true';
        localStorage.removeItem('token');
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('tokenExpiration');
        setUser(null);
        
        if (wasRemembered) {
          toast.error('Your extended session has expired. Please login again.');
        } else {
          toast.error('Session expired. Please login again.');
        }
      }
      return Promise.reject(error);
    }
  );

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await axios.get('/auth/me');
        setUser(response.data.user);
      }
    } catch (error) {
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, rememberMe = false) => {
    try {
      console.log(axios.defaults.baseURL);
      const response = await axios.post('/auth/login', { email, password, rememberMe });
      const { token, user, expiresIn } = response.data;
      
      localStorage.setItem('token', token);
      
      // Store remember me preference and expiration info
      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true');
        localStorage.setItem('tokenExpiration', expiresIn);
      } else {
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('tokenExpiration');
      }
      
      setUser(user);
      // Remove duplicate success message - let the component handle it
      return true;
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      return false;
    }
  };

  const register = async (username, email, password) => {
    try {
      const response = await axios.post('/auth/register', { username, email, password });
      const { token, user } = response.data;
      
      localStorage.setItem('token', token);
      setUser(user);
      // Remove duplicate success message - let the component handle it
      return true;
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('rememberMe');
    localStorage.removeItem('tokenExpiration');
    setUser(null);
    toast.success('Logged out successfully');
  };

  // Helper function to check if user is in a remembered session
  const isRememberedSession = () => {
    return localStorage.getItem('rememberMe') === 'true';
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    checkAuth,
    isRememberedSession
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 